import { useState, useEffect } from 'react'
import { useWeb3React } from '@web3-react/core'
import styled from 'styled-components'
import { Card, CardBody, Button, useTooltip, Flex } from 'uikit'
import { getNow } from 'utils/getNow'
import { useTranslation } from 'react-i18next'
import useLocalDispatch from 'contexts/LocalRedux/useLocalDispatch'
import { BetPosition, NodeLedger, NodeRound } from 'state/types'
import { fetchLedgerData } from 'state/predictions'
import { ROUND_BUFFER } from 'state/predictions/config'
import useToast from 'hooks/useToast'
// import useTheme from 'hooks/useTheme'
import { ToastDescriptionWithTx } from 'components/Toast'
import { useConfig } from 'pages/Predictions/context/ConfigProvider'
import CardFlip from '../CardFlip'
import { formatBnbv2 } from '../../helpers'
import { RoundResultBox, PrizePoolRow } from '../RoundResult'
import MultiplierArrow from './MultiplierArrow'
import CardHeader from './CardHeader'
import SetPositionCard from './SetPositionCard'
import { Image } from 'components'

interface OpenRoundCardProps {
  round: NodeRound
  betAmount?: NodeLedger['amount']
  hasEnteredUp: boolean
  hasEnteredDown: boolean
  bullMultiplier: string
  bearMultiplier: string
}

interface State {
  isSettingPosition: boolean
  position: BetPosition
}

const ArrowButton = styled(Flex)<{ isUp: boolean }>`
  height: 40px;
  width: 100%;
  align-items: center;
  background: ${({ isUp }) => (isUp ? 'rgba(20, 216, 157, 0.05)' : 'rgba(249, 71, 156, 0.05)')};
  border: ${({ isUp }) => (isUp ? '1px solid #14D89D' : '1px solid #F9479C')};
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
  color: ${({ isUp }) => (isUp ? '#14D89D' : '#F9479C')};
  justify-content: center;
  margin-bottom: 10px;
`

const ImgIcon = styled.img`
  width: 16px !important;
  height: 16px !important;
  margin-right: 4px;
`

const Imgs = {
  up: require('assets/images/prediction/up.svg').default,
  down: require('assets/images/prediction/down.svg').default,
}

const OpenRoundCard: React.FC<OpenRoundCardProps> = ({
  round,
  betAmount,
  hasEnteredUp,
  hasEnteredDown,
  bullMultiplier,
  bearMultiplier,
}) => {
  const [state, setState] = useState<State>({
    isSettingPosition: false,
    position: BetPosition.BULL,
  })
  const { t } = useTranslation()
  // const { theme } = useTheme()
  const { toastSuccess } = useToast()
  const { account } = useWeb3React()
  const dispatch = useLocalDispatch()
  const { displayedDecimals, payToken } = useConfig()
  const { lockTimestamp } = round ?? { lockTimestamp: null }
  const { isSettingPosition, position } = state
  const positionDisplay = position === BetPosition.BULL ? <ImgIcon src={Imgs.up} /> : <ImgIcon src={Imgs.down} />
  const { targetRef, tooltipVisible, tooltip } = useTooltip(
    <div style={{ whiteSpace: 'nowrap' }}>{`${formatBnbv2(betAmount, displayedDecimals)} ${payToken.symbol}`}</div>,
    { placement: 'top' }
  )

  useEffect(() => {
    const secondsToLock = lockTimestamp ? lockTimestamp - getNow() : 0
    if (secondsToLock > 0) {
      const setIsBufferPhaseTimeout = setTimeout(() => {}, (secondsToLock - ROUND_BUFFER) * 1000)

      return () => {
        clearTimeout(setIsBufferPhaseTimeout)
      }
    }
    return undefined
  }, [lockTimestamp])

  const getHasEnteredPosition = () => {
    if (hasEnteredUp || hasEnteredDown) {
      return false
    }

    if (round.lockPrice !== null) {
      return false
    }

    return true
  }

  const canEnterPosition = getHasEnteredPosition()

  const handleBack = () =>
    setState((prevState) => ({
      ...prevState,
      isSettingPosition: false,
    }))

  const handleSetPosition = (newPosition: BetPosition) => {
    setState((prevState) => ({
      ...prevState,
      isSettingPosition: true,
      position: newPosition,
    }))
  }

  const togglePosition = () => {
    setState((prevState) => ({
      ...prevState,
      position: prevState.position === BetPosition.BULL ? BetPosition.BEAR : BetPosition.BULL,
    }))
  }

  const handleSuccess = async (hash: string) => {
    await dispatch(fetchLedgerData({ account, epochs: [round.epoch] }))

    handleBack()
    toastSuccess(
      t('PREDICTION_53'),
      <ToastDescriptionWithTx txHash={hash}>{`${t('PREDICTION_54', {
        direction: position === BetPosition.BULL ? t('PREDICTION_10') : t('PREDICTION_11'),
      })}`}</ToastDescriptionWithTx>
    )
  }

  useEffect(() => {
    if (hasEnteredDown) {
      setState((prevState) => ({
        ...prevState,
        isSettingPosition: false,
        position: BetPosition.BEAR,
      }))
    } else if (hasEnteredUp) {
      setState((prevState) => ({
        ...prevState,
        isSettingPosition: false,
        position: BetPosition.BULL,
      }))
    }
  }, [hasEnteredDown, hasEnteredUp])

  // const getPositionEnteredIcon = () => {
  //   return position === BetPosition.BULL ? <ArrowUpIcon color="currentColor" /> : <ArrowDownIcon color="currentColor" />
  // }

  return (
    <CardFlip isFlipped={isSettingPosition} height="404px" width="100%">
      <Card>
        <CardHeader
          status="next"
          epoch={round.epoch}
          icon={<Image src={require('assets/images/prediction/next.svg').default} width="24px" />}
          title={t('PREDICTION_55')}
        />
        <CardBody p="16px">
          <MultiplierArrow betAmount={betAmount} multiplier={bullMultiplier} hasEntered={hasEnteredUp} />
          <RoundResultBox isNext={canEnterPosition} isLive={!canEnterPosition} betPosition={position}>
            {canEnterPosition ? (
              <>
                <PrizePoolRow totalAmount={round.totalAmount} mb="10px" />
                <Button
                  variant="success"
                  width="100%"
                  height="40px"
                  onClick={() => handleSetPosition(BetPosition.BULL)}
                  mb="8px"
                  style={{ borderRadius: '30px' }}
                  disabled={true}
                >
                  {t('PREDICTION_56', { direction: t('PREDICTION_10') })}
                </Button>
                <Button
                  height="40px"
                  width="100%"
                  onClick={() => handleSetPosition(BetPosition.BEAR)}
                  disabled={true}
                  style={{
                    background: 'rgb(201,201,201)',
                    borderRadius: '30px',
                  }}
                >
                  {t('PREDICTION_56', { direction: t('PREDICTION_11') })}
                </Button>
              </>
            ) : (
              <>
                <div ref={targetRef}>
                  {/* <Button disabled width="100%" mb="8px">
                    {`${positionDisplay} Entered`}
                  </Button> */}
                  <ArrowButton isUp={position === BetPosition.BULL}>
                    {positionDisplay} {position === BetPosition.BULL ? t('PREDICTION_10') : t('PREDICTION_11')} Entered
                  </ArrowButton>
                </div>
                <PrizePoolRow totalAmount={round.totalAmount} />
                {tooltipVisible && tooltip}
              </>
            )}
          </RoundResultBox>
          <MultiplierArrow
            betAmount={betAmount}
            multiplier={bearMultiplier}
            betPosition={BetPosition.BEAR}
            hasEntered={hasEnteredDown}
          />
        </CardBody>
      </Card>
      <SetPositionCard
        onBack={handleBack}
        onSuccess={handleSuccess}
        position={position}
        togglePosition={togglePosition}
        epoch={round.epoch}
      />
    </CardFlip>
  )
}

export default OpenRoundCard
